import React, { useState } from 'react';
import { Tooltip, useMediaQuery, useTheme } from '@mui/material';
import {
  Container,
  CountBadge,
  MobileContainer,
  MobileCountBadge,
  ProfilesContainer,
} from './styles';
import { imgUrlChk } from '@/utils/function';
import ImageWithErrorHandling from '@/components/ImageWithErrorHandling';
import {
  ParticipantPerson as Person,
  toExternalPersons,
} from '@/components/Approval/ParticipantsRow/externalParticipants';

type Participants = {
  internal: Person[];
  external: Person[] | string[] | null;
};

const ParticipantsRow = ({ participants }: { participants: Participants }) => {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('xl')); // 1440px 이상
  const [isHovered, setIsHovered] = useState(false);

  // internal + external 합치기
  const allParticipants = [
    ...participants.internal,
    ...toExternalPersons(participants.external),
  ];

  const totalCount = allParticipants.length;

  if (totalCount === 0) return null;

  if (isDesktop) {
    const MAX_DISPLAY = 4;
    const remainingCount = totalCount - MAX_DISPLAY;
    const showRemaining = remainingCount > 0;
    const displayRemainingCount = Math.min(
      showRemaining ? remainingCount + 1 : remainingCount,
      9,
    );
    const displayProfiles = allParticipants.slice(
      0,
      showRemaining ? MAX_DISPLAY - 1 : MAX_DISPLAY,
    );

    return (
      <Container>
        <ProfilesContainer>
          {displayProfiles.map((person, index) => (
            <ImageWithErrorHandling
              key={`${person.passportId}-${index}`}
              src={imgUrlChk(person.profileImageUrl)}
              width={32}
              height={32}
              alt={person.name}
              errorImage={'/images/dummy/default-profile.svg'}
              style={{
                borderRadius: '16px',
                objectFit: 'cover',
                boxShadow: '0 0 0 1px #ffffff',
                marginLeft: index === 0 ? 0 : '-8px',
              }}
            />
          ))}
          {showRemaining && <CountBadge>+{displayRemainingCount}</CountBadge>}
        </ProfilesContainer>
      </Container>
    );
  } else {
    // Mobile: 1개 프로필 + +N 배지
    const MAX_DISPLAY = 1;
    const firstProfile = allParticipants[0];
    const remainingCount = totalCount - MAX_DISPLAY;
    const showRemaining = remainingCount > 0;
    const displayRemainingCount = Math.min(
      showRemaining ? remainingCount + 1 : remainingCount,
      9,
    );

    return (
      <MobileContainer
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <Tooltip
          title={
            <>
              <b>
                {allParticipants
                  .map((p) => `${p.name} ${p.positionName}`)
                  .join(', ')}
              </b>
              님이 참석했습니다.
            </>
          }
          placement="top"
          arrow
          slotProps={{
            tooltip: {
              sx: {
                backgroundColor: '#000',
                color: '#fff',
                fontSize: '14px',
                lineHeight: '24px',
                fontWeight: 400,
                padding: '8px 12px',
                borderRadius: '12px',
                maxWidth: '300px',
              },
            },
            arrow: {
              sx: {
                color: 'var(--dk-text, #000)',
              },
            },
          }}
        >
          <div
            style={{
              position: 'relative',
              width: '32px',
              height: '32px',
              borderRadius: '16px',
              boxShadow: isHovered
                ? '0 0 0 1px #ffffff, 0 0 0 2px #000000'
                : '0 0 0 1px #ffffff',
              marginRight: showRemaining ? '8px' : 0,
            }}
          >
            <ImageWithErrorHandling
              src={imgUrlChk(firstProfile.profileImageUrl)}
              width={32}
              height={32}
              alt={firstProfile.name}
              errorImage={'/images/dummy/default-profile.svg'}
              style={{
                flex: 'none',
                borderRadius: '16px',
                objectFit: 'cover',
              }}
            />
            {showRemaining && (
              <MobileCountBadge>+{displayRemainingCount}</MobileCountBadge>
            )}
          </div>
        </Tooltip>
      </MobileContainer>
    );
  }
};

export default ParticipantsRow;
